import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  FiHome,
  FiPackage,
  FiShoppingCart,
  FiTrendingUp,
  FiBarChart2,
  FiFileText,
  FiAward,
  FiSettings,
  FiLogOut,
  FiActivity,
  FiBriefcase,
  FiAlertTriangle,
  FiUserPlus,
  FiClipboard,
} from 'react-icons/fi';
import { useAuthStore } from '../store/authStore';

const Sidebar = ({ collapsed, onLogout }) => {
  const lraRole = useAuthStore((state) => state.lraRole);
  const lraUser = useAuthStore((state) => state.lraUser);
  const lraDetails = useAuthStore((state) => state.lraDetails);
  const activeView = useAuthStore((state) => state.activeView);

  const isLRAStaff = activeView === 'lra';

  // ---------- Merchant menu ----------
  const merchantMenu = [
    { path: '/dashboard', icon: <FiHome size={20} />, label: 'Dashboard' },
    { path: '/products', icon: <FiPackage size={20} />, label: 'Products' },
    { path: '/stock', icon: <FiShoppingCart size={20} />, label: 'Stock' },
    { path: '/sales', icon: <FiTrendingUp size={20} />, label: 'Sales' },
  ];

  const merchantTax = [
    {
      path: '/tax/summary',
      icon: <FiFileText size={20} />,
      label: 'Tax Summary',
    },
    { path: '/tax/filing', icon: <FiBarChart2 size={20} />, label: 'Filing' },
    {
      path: '/tax/clearance',
      icon: <FiAward size={20} />,
      label: 'Tax Clearance',
    },
  ];

  const merchantBottom = [
    { path: '/reports', icon: <FiBarChart2 size={20} />, label: 'Reports' },
    { path: '/settings', icon: <FiSettings size={20} />, label: 'Settings' },
  ];

  // ---------- LRA menu ----------
  const lraMain = [
    {
      path: '/lra/intelligence',
      icon: <FiActivity size={20} />,
      label: 'Intelligence',
    },
    {
      path: '/lra/businesses',
      icon: <FiBriefcase size={20} />,
      label: 'Businesses',
    },
    {
      path: '/lra/audit-signals',
      icon: <FiAlertTriangle size={20} />,
      label: 'Audit Signals',
    },
  ];

  const lraRegistration = [
    {
      path: '/lra/registration/new',
      icon: <FiUserPlus size={20} />,
      label: 'New Registration',
    },
    {
      path: '/lra/registration/history',
      icon: <FiClipboard size={20} />,
      label: 'History',
    },
  ];

  const lraBottom = [
    {
      path: '/lra/reports',
      icon: <FiBarChart2 size={20} />,
      label: 'Reports',
    },
    { path: '/settings', icon: <FiSettings size={20} />, label: 'Settings' },
  ];

  const renderItem = (item) => (
    <NavLink
      key={item.path}
      to={item.path}
      className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
    >
      {item.icon}
      {!collapsed && <span className="nav-label">{item.label}</span>}
    </NavLink>
  );

  const renderSectionHeader = (label) => {
    if (collapsed) {
      return (
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.1)',
            margin: '12px 0',
          }}
        />
      );
    }
    return (
      <div
        style={{
          padding: '16px 20px 8px',
          fontSize: '11px',
          fontWeight: 600,
          color: 'rgba(255,255,255,0.4)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {label}
      </div>
    );
  };

  // ---------- Header label ----------
  const getUserLabel = () => {
    if (isLRAStaff) {
      if (lraDetails?.title) return lraDetails.title;
      if (lraRole === 'admin') return 'Administrator';
      if (lraRole === 'officer') return 'Officer';
      if (lraRole === 'agent') return 'Field Agent';
      return 'LRA Staff';
    }
    return 'Merchant';
  };

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div
          className="sidebar-logo"
          style={{
            background: isLRAStaff ? '#dc2626' : '#1a73e8',
          }}
        >
          {isLRAStaff ? 'LRA' : 'DL'}
        </div>
        {!collapsed && (
          <div>
            <div className="sidebar-title">
              {isLRAStaff ? 'LRA Officer Portal' : 'LRA Inventory'}
            </div>
            <div className="sidebar-subtitle">
              {isLRAStaff
                ? lraUser?.first_name
                  ? `${lraUser.first_name} ${lraUser.last_name || ''}`.trim()
                  : 'Revenue Authority'
                : 'Liberia Revenue Authority'}
            </div>
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        {isLRAStaff ? (
          <>
            {/* LRA sidebar */}
            {!collapsed && (
              <div
                style={{
                  padding: '12px 20px',
                  fontSize: '11px',
                  color: 'rgba(255,255,255,0.5)',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div style={{ fontWeight: 600, color: '#ffffff' }}>
                  {getUserLabel()}
                </div>
                <div style={{ marginTop: 2 }}>
                  {lraRole === 'admin'
                    ? 'Full Access'
                    : lraRole === 'officer'
                    ? 'Officer Access'
                    : 'Agent Access'}
                </div>
              </div>
            )}

            {lraMain.map(renderItem)}

            {renderSectionHeader('Registration')}
            {lraRegistration.map(renderItem)}

            {renderSectionHeader('Analytics')}
            {lraBottom.map(renderItem)}
          </>
        ) : (
          <>
            {/* Merchant sidebar */}
            {merchantMenu.map(renderItem)}

            {renderSectionHeader('Tax & Compliance')}
            {merchantTax.map(renderItem)}

            {renderSectionHeader('Analytics')}
            {merchantBottom.map(renderItem)}
          </>
        )}
      </nav>

      <div
        style={{
          padding: '16px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <div className="nav-item" onClick={onLogout}>
          <FiLogOut size={20} />
          {!collapsed && <span className="nav-label">Logout</span>}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
